import React, { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
  fetchHostPendingRequests,
  updateRequestStatus,
} from 'src/redesign/features/Attendees/store/actions';
import { hostPendingRequestsSelector } from 'src/redesign/features/Attendees/store/selectors';
import { setCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { CONNECTED_MODALS } from 'src/redesign/features/Modals/store/constants';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Text } from '@components/Text';
import moment from 'moment';
import S from './NotificationBell.module.scss';

export const NotificationBell: React.FC = () => {
  const dispatch = useAppDispatch();
  const hostPendingRequests = useAppSelector(hostPendingRequestsSelector);
  const overlayPanelRef = useRef<OverlayPanel>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch pending requests on component mount and periodically
  useEffect(() => {
    dispatch(fetchHostPendingRequests());
  }, []);

  const pendingCount = hostPendingRequests?.length || 0;

  const handleBellClick = (e: React.MouseEvent) => {
    if (pendingCount > 0) {
      overlayPanelRef.current?.toggle(e);
    }
  };

  const handleSeeMoreClick = () => {
    overlayPanelRef.current?.hide();
    dispatch(
      setCurrentModalAction({
        currentModal: CONNECTED_MODALS.HOST_NOTIFICATIONS_MODAL,
        data: {
          title: 'Pending Requests',
          requests: hostPendingRequests || [],
        },
      }),
    );
  };

  const handleRequestClick = (request: any) => {
    overlayPanelRef.current?.hide();

    // Handle contact messages
    if (request.type === 'CONTACT_MESSAGE') {
      dispatch(
        setCurrentModalAction({
          currentModal: CONNECTED_MODALS.CONTACT_MESSAGE_MODAL,
          data: {
            title: 'Contact Message',
            message: request,
          },
        }),
      );
      return;
    }

    // Handle refund/transfer requests
    const modalType =
      request.type === 'refund'
        ? CONNECTED_MODALS.CANCEL_REFUND_REQUEST_MODAL
        : CONNECTED_MODALS.TRANSFER_REQUEST_MODAL;

    dispatch(
      setCurrentModalAction({
        currentModal: modalType,
        data: {
          title: request.type === 'refund' ? 'Cancellation & Refund Request' : 'Transfer Request',
          request,
          onApprove: handleApproveRequest,
          onDeny: handleDenyRequest,
        },
      }),
    );
  };

  const handleApproveRequest = async (
    requestId: string,
    comment: string,
    ticketSelections?: Record<string, string>,
  ) => {
    await dispatch(
      updateRequestStatus({
        requestId,
        status: 'approved',
        admin_notes: comment || undefined,
        ticket_assignments: ticketSelections,
      }),
    );
    // Refresh the pending requests
    dispatch(fetchHostPendingRequests());
  };

  const handleDenyRequest = async (requestId: string, comment: string) => {
    await dispatch(
      updateRequestStatus({
        requestId,
        status: 'denied',
        admin_notes: comment || undefined,
      }),
    );
    // Refresh the pending requests
    dispatch(fetchHostPendingRequests());
  };

  const getRequestText = (request: any) => {
    if (request.type === 'CONTACT_MESSAGE') {
      return `Message from ${request.sender.name}`;
    }

    const attendeeName = `${request.attendees[0]?.first_name} ${request.attendees[0]?.last_name}`;

    if (request.type === 'refund') {
      return `${attendeeName} wants to cancel and request a refund`;
    } else {
      return `${attendeeName} wants to transfer to another event`;
    }
  };

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const renderDropdownContent = () => {
    if (!hostPendingRequests || hostPendingRequests.length === 0) {
      return (
        <div className={S.dropdownContent}>
          <Text variation="body-small" color="black-400">
            No pending requests
          </Text>
        </div>
      );
    }

    // Show max 3 requests when collapsed, all when expanded
    const displayRequests = isExpanded ? hostPendingRequests : hostPendingRequests.slice(0, 3);
    const hasMoreRequests = hostPendingRequests.length > 3;

    return (
      <div className={S.dropdownContent}>
        <div className={S.dropdownHeader}>
          <Text variation="body-small" bold>
            Pending Requests
          </Text>
        </div>

        <div className={`${S.requestsList} ${isExpanded ? S.expanded : ''}`}>
          {displayRequests.map((request) => (
            <div
              key={request.id}
              className={S.requestItem}
              onClick={() => handleRequestClick(request)}
            >
              <Text variation="body-small" bold className={S.requestText}>
                {getRequestText(request)}
              </Text>
              {request.type === 'CONTACT_MESSAGE' ? (
                <Text variation="caption" color="black-400" className={S.reasonText}>
                  {request.subject}
                </Text>
              ) : (
                <Text variation="caption" color="black-400" className={S.reasonText}>
                  Reason:{' '}
                  {request.reason.length > 50
                    ? `${request.reason.substring(0, 50)}...`
                    : request.reason}
                </Text>
              )}
              <Text variation="caption" color="black-400" className={S.timeText}>
                {moment(request.created_at).fromNow()}
              </Text>
            </div>
          ))}
        </div>

        {hasMoreRequests && !isExpanded && (
          <div className={S.moreRequestsText} onClick={handleExpandToggle}>
            <Text variation="caption" color="brand-400" className={S.expandButton}>
              +{hostPendingRequests.length - 3} more request
              {hostPendingRequests.length - 3 > 1 ? 's' : ''}
            </Text>
          </div>
        )}

        {isExpanded && hasMoreRequests && (
          <div className={S.moreRequestsText} onClick={handleExpandToggle}>
            <Text variation="caption" color="brand-400" className={S.expandButton}>
              Show less
            </Text>
          </div>
        )}

        <div className={S.dropdownFooter}>
          <Button label="See More" className={S.seeMoreButton} onClick={handleSeeMoreClick} text />
        </div>
      </div>
    );
  };

  return (
    <div className={S.notificationBell}>
      <Button icon="pi pi-bell" className={S.bellButton} onClick={handleBellClick} text />
      {pendingCount > 0 && (
        <Badge value={pendingCount.toString()} severity="danger" className={S.badge} />
      )}

      <OverlayPanel ref={overlayPanelRef} className={S.overlay} dismissable>
        {renderDropdownContent()}
      </OverlayPanel>
    </div>
  );
};
